import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  DD,
  DescriptionList,
  DT,
  LoadingBlock,
  PageContainer,
  PageHeader,
} from '../../components/ui';
import {
  useJobTimelineQuery,
  useMyWorkerJobsQuery,
} from '../../services/serviceService/__generated__/queries.generated';
import { jobItemLabels, jobStatusView, windowText } from '../ServiceGuestPage/serviceJob';
import { JobAccessCard } from '../ServiceJobPage/JobAccessCard';
import { HandoverClock } from '../ServiceJobPage/HandoverClock';
import { JobTimelineView } from '../ServiceJobPage/JobTimelineView';
import { ServiceReviewCard } from '../ServiceJobPage/ServiceReviewCard';
import { completedAtOf } from '../ServiceJobPage/jobTimeline';
import { WorkerJobControls } from '../ServiceJobPage/WorkerJobControls';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { WORKER_JOBS_PATH } from './WorkerJobsPage';

const Columns = styled.div`
  ${tw`grid grid-cols-1 gap-6 lg:grid-cols-3`}
`;

const Main = styled.div`
  ${tw`flex flex-col gap-6 lg:col-span-2 min-w-0`}
`;

const Side = styled.div`
  ${tw`flex flex-col gap-6 min-w-0`}
`;

/**
 * One job for its worker on the web (spec 019 W19): the window and the place,
 * the address and door code from 2 hours before, the check-ins, checklist
 * photos, a problem report and Finish, and the timeline.
 */
export default function WorkerJobPage() {
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const jobs = useMyWorkerJobsQuery({
    variables: { date: null },
    fetchPolicy: 'cache-and-network',
  });
  const timeline = useJobTimelineQuery({
    variables: { bookingId },
    fetchPolicy: 'cache-and-network',
  });
  const job = jobs.data?.myWorkerJobs.find(j => j.id === bookingId) ?? null;

  if ((jobs.loading && !jobs.data) || (timeline.loading && !timeline.data)) {
    return <LoadingBlock label="Loading the job" />;
  }
  if (!timeline.data) {
    return (
      <PageContainer width="md">
        <Alert tone="error" title="Job not found">
          {timeline.error ? stayErrorMessage(timeline.error) : 'This job is not yours.'}{' '}
          <Link to={WORKER_JOBS_PATH}>Your jobs</Link>
        </Alert>
      </PageContainer>
    );
  }
  const info = job?.serviceJob ?? null;
  const zone = info?.timezone ?? 'America/Phoenix';
  const status = job ? jobStatusView(job) : null;
  const home = (info?.site?.homeDetails ?? null) as Record<string, any> | null;
  const vehicle = (info?.site?.vehicleDetails ?? null) as Record<string, any> | null;

  return (
    <PageContainer width="xl" canvas data-testid="worker-job">
      <PageHeader
        eyebrow="Job"
        title={jobItemLabels(info?.items).join(' + ') || 'Job'}
        description={info ? windowText(info.windowStartAt, info.windowEndAt, zone) : undefined}
        backTo={{ to: WORKER_JOBS_PATH, label: 'Your jobs' }}
        actions={status ? <Badge tone={status.tone}>{status.label}</Badge> : undefined}
      />
      <Columns>
        <Main>
          <HandoverClock
            minutesSoFar={timeline.data.jobTimeline.handoverMinutesSoFar}
            maxBillableMinutes={info?.maxBillableMinutes}
          />
          {!info && timeline.data.jobTimeline.customerNote && (
            <Alert tone="info" title="Note from the customer">
              {timeline.data.jobTimeline.customerNote}
            </Alert>
          )}
          <WorkerJobControls
            bookingId={bookingId}
            timeline={timeline.data.jobTimeline}
            onRecorded={() => jobs.refetch()}
          />
          <ServiceReviewCard
            bookingId={bookingId}
            reviewer="worker"
            jobStatus={timeline.data.jobTimeline.jobStatus}
            completedAt={info?.completedAt ?? completedAtOf(timeline.data.jobTimeline.events)}
            otherName={job?.guestDisplayName ?? 'the customer'}
            zone={zone}
          />
          <JobTimelineView timeline={timeline.data.jobTimeline} zone={zone} />
        </Main>
        <Side>
          <JobAccessCard bookingId={bookingId} zone={zone} />
          {info && (
            <Card>
              <CardHeader title="The job" />
              <CardBody>
                <DescriptionList>
                  <DT>Where</DT>
                  <DD>
                    {info.site?.city} {info.site?.zip}
                  </DD>
                  {home && (
                    <>
                      <DT>Home</DT>
                      <DD>
                        {home.bedrooms === 0 ? 'Studio' : `${home.bedrooms} bd`} · {home.bathrooms}{' '}
                        ba
                        {home.pets ? ' · pets' : ''}
                      </DD>
                    </>
                  )}
                  {vehicle && (
                    <>
                      <DT>Vehicle</DT>
                      <DD>
                        {[vehicle.year, vehicle.make, vehicle.model, vehicle.color, vehicle.plate]
                          .filter(Boolean)
                          .join(' ')}
                      </DD>
                    </>
                  )}
                  {(info.customerNote ?? timeline.data.jobTimeline.customerNote) && (
                    <>
                      <DT>Note</DT>
                      <DD>{info.customerNote ?? timeline.data.jobTimeline.customerNote}</DD>
                    </>
                  )}
                </DescriptionList>
              </CardBody>
            </Card>
          )}
        </Side>
      </Columns>
    </PageContainer>
  );
}
